import { Fragment, useCallback, useEffect, useState } from 'react';
import { Download, Pause, Play } from 'lucide-react';
import DirIcon from '../components/DirIcon.jsx';
import DateRangePicker from '../components/DateRangePicker.jsx';
import Pill from '../components/Pill.jsx';
import { api } from '../lib/api.js';
import { defaultRange, localDate, prettyDate } from '../lib/dateRange.js';
import {
  STATUS_LABELS, STATUS_TONES, formatDuration, formatNumber, formatWhen,
} from '../lib/format.js';
import { useSession, ROLES } from '../lib/session.jsx';

const FILTERS = [
  ['all', 'All'],
  ['in', 'Inbound'],
  ['out', 'Outbound'],
  ['missed', 'Missed'],
  ['internal', 'Internal'],
];

const PER_PAGE = 25;

/* One row per call — not per leg.

   A call to the main number rings every extension in the queue and the PBX
   records each as its own row. Showing those raw turns one call into eight
   and makes the history unreadable. The backend collapses them; this shows
   the call and who picked it up. */
export default function CallHistory({ extension = null }) {
  const { role } = useSession();
  const [direction, setDirection] = useState('all');
  const [range, setRange] = useState(defaultRange); // today, by default
  const [page, setPage] = useState(1);
  const [playing, setPlaying] = useState(null); // call id whose recording is open
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  const load = useCallback((signal) => {
    setState((s) => ({ ...s, status: 'loading' }));

    const qs = new URLSearchParams({
      direction,
      from: range.from,
      to: range.to,
      page: String(page),
      per_page: String(PER_PAGE),
    });
    // Only an admin's request is honoured; the backend pins an extension user
    // to their own regardless of what turns up here.
    if (extension) qs.set('extension', extension);

    api
      .get(`/calls?${qs}`, { signal })
      .then((data) => setState({ status: 'ready', data, error: null }))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setState({ status: 'error', data: null, error: err });
      });
  }, [direction, range.from, range.to, page, extension]);

  // Any change to the filters starts the reader back at page 1 — page 3 of the
  // old range is meaningless against the new one.
  const changeRange = (next) => { setPage(1); setRange(next); };
  const changeDirection = (next) => { setPage(1); setDirection(next); };

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const rows = state.data?.items ?? [];
  const pagination = state.data?.pagination;
  const filters = state.data?.filters;
  // "You" only makes sense when the viewer *is* the extension. An admin
  // looking at Dana's calls wants to read "Dana".
  const mine = role === ROLES.EXTENSION_USER && !extension;

  const exportCsv = () => {
    const header = ['When', 'Direction', 'From', 'To', 'Extension', 'Talk time (s)', 'Status'];
    const body = rows.map((r) => [
      r.started_at, r.direction, r.from, r.to,
      r.extension_name ? `${r.extension_name} (${r.extension})` : '',
      r.duration, STATUS_LABELS[r.status] ?? r.status,
    ]);
    const csv = [header, ...body]
      .map((line) => line.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-page-${page}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className="btn btn--filter"
              aria-pressed={direction === key}
              onClick={() => changeDirection(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <button type="button" className="btn" onClick={exportCsv} disabled={rows.length === 0}>
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <DateRangePicker range={range} onChange={changeRange} />
      </div>

      <div className="card">
        {state.status === 'error' && (
          <div className="empty">
            <div className="empty__title">We couldn't load your calls</div>
            <p className="empty__body">{state.error?.message}</p>
          </div>
        )}

        {state.status === 'loading' && (
          <div className="empty"><p className="empty__body">Loading your calls…</p></div>
        )}

        {state.status === 'ready' && rows.length === 0 && (
          <div className="empty">
            <div className="empty__title">No calls to show</div>
            <p className="empty__body">There are no calls matching this filter in the period shown.</p>
          </div>
        )}

        {state.status === 'ready' && rows.length > 0 && (
          <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th aria-label="Direction" />
                <th>From</th>
                <th>To</th>
                <th>Extension</th>
                <th>Talk time</th>
                <th>Status</th>
                <th>When</th>
                <th aria-label="Recording" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = playing === r.id;
                const recUrl = r.has_recording
                  ? `/api/recordings/${r.recording_id}?on=${localDate(r.started_at)}`
                  : null;
                return (
                  <Fragment key={r.id}>
                    <tr>
                      <td className="td--icon"><DirIcon dir={r.direction} /></td>
                      <td>
                        <b style={{ fontWeight: 600 }}>{formatNumber(r.from)}</b>
                        {r.from_name && (
                          <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{r.from_name}</div>
                        )}
                      </td>
                      <td className="td--soft">{formatNumber(r.to)}</td>
                      <td><ExtensionCell call={r} mine={mine} /></td>
                      <td className="td--mono">{formatDuration(r.duration)}</td>
                      <td><Pill tone={STATUS_TONES[r.status] ?? 'offline'}>{STATUS_LABELS[r.status] ?? r.status}</Pill></td>
                      <td className="td--soft" style={{ fontSize: 13 }}>{formatWhen(r.started_at)}</td>
                      <td className="td--icon">
                        {recUrl && (
                          <button
                            type="button"
                            aria-label={open ? 'Hide recording' : 'Play recording'}
                            title={open ? 'Hide recording' : 'Play recording'}
                            onClick={() => setPlaying(open ? null : r.id)}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                          >
                            {open ? <Pause size={15} color="var(--signal)" /> : <Play size={15} color="var(--signal)" />}
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && recUrl && (
                      <tr>
                        <td colSpan={8} style={{ padding: '4px 16px 12px', background: 'var(--signal-wash)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {/* Streamed through the portal — same-origin, so the
                                session cookie rides along and the PBX URL stays hidden. */}
                            <audio src={recUrl} controls autoPlay style={{ flex: 1, height: 36 }} />
                            <a href={`${recUrl}&download=1`} className="btn" title="Download recording">
                              <Download size={14} /> Download
                            </a>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {pagination && pagination.pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Previous
          </button>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink-soft)' }}>
            Page {pagination.page} of {pagination.pages} · {pagination.total} calls
          </span>
          <button type="button" className="btn" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {filters && (
        <p className="footnote">
          {filters.from === filters.to
            ? prettyDate(filters.from)
            : `${prettyDate(filters.from)} – ${prettyDate(filters.to)}`}
          . A call that rings several extensions is shown once, against whoever picked it up.
        </p>
      )}
    </div>
  );
}

/* The extension on our side of the call — whoever answered an inbound or
   placed an outbound. Direction-neutral on purpose: "Answered by" was wrong
   for outbound, where nobody answered, the extension dialled. An outbound
   call always carries its placing extension (recovered from the channel), so
   the "nobody picked up" branch below only ever fires for inbound. */
function ExtensionCell({ call, mine }) {
  if (call.status === 'voicemail') {
    return <span style={{ color: 'var(--ink-soft)', fontSize: 13 }}>Voicemail</span>;
  }
  if (call.status === 'answered_elsewhere') {
    // Someone took it, we just can't see who: a scoped view contains only
    // this extension's leg, not the colleague's.
    return <span style={{ color: 'var(--ink-soft)', fontSize: 13 }}>A colleague</span>;
  }
  if (!call.extension) {
    // Nobody picked up. How many phones rang answers the obvious follow-up —
    // "did it even ring?" — but only when we can actually see the fan-out.
    // On a single-extension view `rang` is 1 by construction, and reporting
    // that would misdescribe a call that rang the whole team.
    return (
      <span style={{ color: 'var(--ink-soft)', fontSize: 13 }}>
        {call.rang > 1 ? `Rang ${call.rang} extensions` : 'No answer'}
      </span>
    );
  }
  if (mine) {
    return <span style={{ fontSize: 13.5 }}>You</span>;
  }

  // Number on top, name beneath — the extension is the identifier people scan
  // for, the name is the gloss.
  return (
    <span style={{ display: 'block', lineHeight: 1.35 }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--signal-deep)' }}>
        {call.extension}
      </span>
      {call.extension_name && (
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>
          {call.extension_name}
        </span>
      )}
    </span>
  );
}
